import { useEffect, useRef } from 'react';

/**
 * Ask before leaving a screen with unsaved changes.
 *
 * The app runs on <BrowserRouter> (not a data router), so react-router's
 * useBlocker isn't available. Two cases are covered instead:
 *   - closing the tab, reloading, or going to another site: the browser's own
 *     "Leave site?" prompt (beforeunload);
 *   - following any in-app link (the "← Content" link, the sidebar, a tab):
 *     a click on an <a href> anywhere on the page asks first. It listens in the
 *     capture phase, so it runs before react-router's Link handler and can stop
 *     the navigation.
 * The browser's Back button inside the app is not caught.
 *
 * Several editors on one screen can each call it; any one of them being dirty
 * is enough to ask.
 */
export function useLeaveGuard(
  dirty: boolean,
  message = 'You have unsaved changes. Leave without saving?'
) {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers need a returnValue for the prompt to show.
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // a new tab or window
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const to = new URL(a.href, window.location.href);
      const here = window.location;
      // Same page, another #section: nothing is lost.
      if (to.pathname === here.pathname && to.search === here.search) return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, message]);
}
